/**
 * Auth service layer — all API calls related to authentication.
 * Tokens are stored in memory only (never localStorage).
 */

const API_BASE = '/api';

interface RegisterResponse {
  email_confirmation_required: true;
  message: string;
  user_id: string;
}

interface ConfirmEmailResponse {
  confirmed: boolean;
  message: string;
}

interface LoginResponse {
  id_token: string;
  access_token: string;
  token_type: string;
  expires_in: number;
  // Present if MFA challenge (future use)
  challenge?: string;
  session?: string;
}

interface ResetPasswordResponse {
  success: boolean;
}

export async function register(email: string, password: string, nickname: string): Promise<RegisterResponse> {
  const res = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, nickname }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Registration failed' }));
    throw new Error(err.message || err.error || 'Registration failed');
  }
  return res.json();
}

export async function confirmEmail(email: string, code: string): Promise<ConfirmEmailResponse> {
  const res = await fetch(`${API_BASE}/auth/confirm-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Email confirmation failed' }));
    throw new Error(err.message || err.error || 'Email confirmation failed');
  }
  return res.json();
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include', // receive HttpOnly refresh token cookie
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Authentication failed' }));
    throw new Error(err.message || err.error || 'Authentication failed');
  }
  return res.json();
}

export async function forgotPassword(email: string): Promise<{ success: boolean }> {
  const res = await fetch(`${API_BASE}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(err.message || 'Request failed');
  }
  return res.json();
}

export async function resetPassword(email: string, code: string, newPassword: string): Promise<ResetPasswordResponse> {
  const res = await fetch(`${API_BASE}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, new_password: newPassword }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Password reset failed' }));
    throw new Error(err.message || 'Password reset failed');
  }
  return res.json();
}

export async function setAvatar(accessToken: string, avatar: string): Promise<{ success: boolean }> {
  const res = await fetch(`${API_BASE}/profile/avatar`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ avatar }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Avatar update failed' }));
    throw new Error(err.message || 'Avatar update failed');
  }
  return res.json();
}
